import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft, Shield, ShieldCheck, ShieldOff, Lock, Eye, EyeOff, 
  Smartphone, Copy, CheckCircle2, Send, Link2, Unlink, ExternalLink
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Settings({ user, setUser }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [setupData, setSetupData] = useState(null);
  const [step, setStep] = useState('main'); // main, setup, verify, disable
  
  // Telegram linking state
  const [telegramStatus, setTelegramStatus] = useState(null);
  const [telegramId, setTelegramId] = useState('');
  const [linkingTelegram, setLinkingTelegram] = useState(false);

  // Fetch Telegram link status on mount
  useEffect(() => {
    const fetchTelegramStatus = async () => {
      try {
        const { data } = await axios.get(`${API}/api/telegram/link-status?user_id=${user.id}`);
        setTelegramStatus(data);
      } catch (e) {
        console.error('Failed to fetch Telegram status:', e);
      }
    };
    if (user?.id) {
      fetchTelegramStatus();
    }
  }, [user?.id]);

  const handleLinkTelegram = async () => {
    if (!telegramId || !/^\d+$/.test(telegramId)) {
      toast.error('Please enter a valid Telegram ID (numbers only)');
      return;
    }

    setLinkingTelegram(true);
    try {
      await axios.post(
        `${API}/api/telegram/link?user_id=${user.id}`,
        { telegram_id: parseInt(telegramId) }
      );
      toast.success('Telegram account linked! Check Telegram for confirmation.');
      setTelegramStatus({
        linked: true,
        telegram_id: parseInt(telegramId),
        bot_username: 'Dhavalrailbot'
      });
      setTelegramId('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to link Telegram');
    } finally {
      setLinkingTelegram(false);
    }
  };

  const handleUnlinkTelegram = async () => {
    if (!confirm('Are you sure you want to unlink your Telegram account?')) return;
    
    setLinkingTelegram(true);
    try {
      await axios.post(`${API}/api/telegram/unlink?user_id=${user.id}`);
      toast.success('Telegram account unlinked');
      setTelegramStatus({ linked: false, bot_username: 'Dhavalrailbot' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to unlink Telegram');
    } finally {
      setLinkingTelegram(false);
    }
  };

  const handleSetup2FA = async () => {
    if (!password) {
      toast.error('Please enter your password');
      return;
    }

    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/api/auth/setup-2fa`,
        { password },
        { withCredentials: true }
      );
      setSetupData(data);
      setStep('verify');
      setPassword('');
      toast.success('Scan the QR code with your authenticator app');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to setup 2FA');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async () => {
    if (!totpCode || totpCode.length !== 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API}/api/auth/verify-2fa`,
        { code: totpCode },
        { withCredentials: true }
      );
      setUser({ ...user, totp_enabled: true });
      setStep('main');
      setTotpCode('');
      setSetupData(null);
      toast.success('2FA enabled successfully!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    if (!totpCode || totpCode.length !== 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API}/api/auth/disable-2fa`,
        { code: totpCode },
        { withCredentials: true }
      );
      setUser({ ...user, totp_enabled: false });
      setStep('main');
      setTotpCode('');
      toast.success('2FA disabled');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const copySecret = () => {
    if (setupData?.secret) {
      navigator.clipboard.writeText(setupData.secret);
      toast.success('Secret copied to clipboard');
    }
  };

  const renderMain = () => (
    <div className="space-y-6">
      {/* 2FA Status Card */}
      <Card className="bg-zinc-800/50 border-zinc-700">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {user?.totp_enabled ? (
                <ShieldCheck className="w-8 h-8 text-emerald-400" />
              ) : (
                <Shield className="w-8 h-8 text-zinc-400" />
              )}
              <div>
                <CardTitle className="text-lg text-white">Two-Factor Authentication</CardTitle>
                <CardDescription className="text-zinc-400">
                  Add an extra layer of security to your account
                </CardDescription>
              </div>
            </div>
            <Badge 
              variant="outline" 
              className={user?.totp_enabled 
                ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" 
                : "bg-zinc-700/50 text-zinc-400 border-zinc-600"
              }
            >
              {user?.totp_enabled ? 'Enabled' : 'Disabled'}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          {user?.totp_enabled ? (
            <div className="space-y-4">
              <p className="text-sm text-zinc-400">
                Your account is protected with two-factor authentication. 
                You'll need your authenticator app to sign in.
              </p>
              <Button
                variant="outline"
                className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                onClick={() => setStep('disable')}
              >
                <ShieldOff className="w-4 h-4 mr-2" />
                Disable 2FA
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-zinc-400">
                Enable two-factor authentication to protect your account. 
                You'll need an authenticator app like Google Authenticator or Authy.
              </p>
              <Button
                className="bg-blue-600 hover:bg-blue-700"
                onClick={() => setStep('setup')}
              >
                <Shield className="w-4 h-4 mr-2" />
                Enable 2FA
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Account Info */}
      <Card className="bg-zinc-800/50 border-zinc-700">
        <CardHeader>
          <CardTitle className="text-lg text-white">Account Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex justify-between items-center py-2 border-b border-zinc-700">
            <span className="text-zinc-400">Email</span>
            <span className="text-white">{user?.email}</span>
          </div>
          <div className="flex justify-between items-center py-2 border-b border-zinc-700">
            <span className="text-zinc-400">Name</span>
            <span className="text-white">{user?.name}</span>
          </div>
          <div className="flex justify-between items-center py-2">
            <span className="text-zinc-400">Login Method</span>
            <Badge variant="outline" className="text-zinc-300">
              {user?.auth_type === 'google' ? 'Google' : 'Email/Password'}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Telegram Integration */}
      <Card className="bg-zinc-800/50 border-zinc-700">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Send className={`w-8 h-8 ${telegramStatus?.linked ? 'text-sky-400' : 'text-zinc-400'}`} />
              <div>
                <CardTitle className="text-lg text-white">Telegram Bot</CardTitle>
                <CardDescription className="text-zinc-400">
                  Get PNR updates directly on Telegram
                </CardDescription>
              </div>
            </div>
            <Badge 
              variant="outline" 
              className={telegramStatus?.linked 
                ? "bg-sky-500/20 text-sky-400 border-sky-500/30" 
                : "bg-zinc-700/50 text-zinc-400 border-zinc-600"
              }
            >
              {telegramStatus?.linked ? 'Linked' : 'Not Linked'}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          {telegramStatus?.linked ? (
            <div className="space-y-4">
              <div className="bg-zinc-900 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <span className="text-zinc-400 text-sm">Telegram ID</span>
                  <span className="text-white font-mono">{telegramStatus.telegram_id}</span>
                </div>
                {telegramStatus.telegram_username && (
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-zinc-400 text-sm">Username</span>
                    <span className="text-white">@{telegramStatus.telegram_username}</span>
                  </div>
                )}
              </div>
              <p className="text-sm text-zinc-400">
                You'll receive PNR status updates on Telegram when your ticket status changes.
              </p>
              <div className="flex gap-3">
                <a 
                  href={`https://t.me/${telegramStatus.bot_username || 'Dhavalrailbot'}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button variant="outline" className="border-sky-500/50 text-sky-400 hover:bg-sky-500/10">
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Open Bot
                  </Button>
                </a>
                <Button
                  variant="outline"
                  className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                  onClick={handleUnlinkTelegram}
                  disabled={linkingTelegram}
                >
                  <Unlink className="w-4 h-4 mr-2" />
                  Unlink
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-zinc-900 rounded-lg p-4">
                <h4 className="text-white font-medium mb-3">How to get your Telegram ID:</h4>
                <ol className="text-sm text-zinc-400 space-y-2 list-decimal list-inside">
                  <li>Open Telegram and search for <a href="https://t.me/userinfobot" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline">@userinfobot</a></li>
                  <li>Send /start to the bot</li>
                  <li>Copy the numeric ID it shows you</li>
                  <li>Paste it below and click Link</li>
                </ol>
              </div>
              
              <div className="space-y-2">
                <Label className="text-zinc-300">Your Telegram ID</Label>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    placeholder="e.g. 123456789"
                    value={telegramId}
                    onChange={(e) => setTelegramId(e.target.value.replace(/\D/g, ''))}
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                  <Button
                    className="bg-sky-600 hover:bg-sky-700"
                    onClick={handleLinkTelegram}
                    disabled={linkingTelegram || !telegramId}
                  >
                    {linkingTelegram ? 'Linking...' : (
                      <>
                        <Link2 className="w-4 h-4 mr-2" />
                        Link
                      </>
                    )}
                  </Button>
                </div>
              </div>
              
              <p className="text-xs text-zinc-500">
                After linking, you'll receive a confirmation message on Telegram. 
                You can also send PNR numbers directly to{' '}
                <a 
                  href="https://t.me/Dhavalrailbot" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sky-400 hover:underline"
                >
                  @Dhavalrailbot
                </a>
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );

  const renderSetup = () => (
    <Card className="bg-zinc-800/50 border-zinc-700">
      <CardHeader>
        <CardTitle className="text-lg text-white flex items-center gap-2">
          <Lock className="w-5 h-5" />
          Confirm Your Password
        </CardTitle>
        <CardDescription className="text-zinc-400">
          Enter your password to begin 2FA setup
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label className="text-zinc-300">Password</Label>
          <div className="relative">
            <Input
              type={showPassword ? 'text' : 'password'}
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="bg-zinc-800 border-zinc-700 text-white pr-10"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>

        <div className="flex gap-3">
          <Button
            variant="ghost"
            className="text-zinc-400"
            onClick={() => {
              setStep('main');
              setPassword('');
            }}
          >
            Cancel
          </Button>
          <Button
            className="bg-blue-600 hover:bg-blue-700"
            onClick={handleSetup2FA}
            disabled={loading || !password}
          >
            {loading ? 'Please wait...' : 'Continue'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  const renderVerify = () => (
    <Card className="bg-zinc-800/50 border-zinc-700">
      <CardHeader>
        <CardTitle className="text-lg text-white flex items-center gap-2">
          <Smartphone className="w-5 h-5" />
          Scan QR Code
        </CardTitle>
        <CardDescription className="text-zinc-400">
          Scan this QR code with your authenticator app
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* QR Code */}
        {setupData?.qr_code && (
          <div className="flex justify-center">
            <div className="bg-white p-4 rounded-lg">
              <img src={setupData.qr_code} alt="2FA QR Code" className="w-48 h-48" />
            </div>
          </div>
        )}

        {/* Manual Entry Secret */}
        <div className="bg-zinc-900 rounded-lg p-4">
          <p className="text-xs text-zinc-400 mb-2">Or enter this code manually:</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-sm text-emerald-400 font-mono bg-zinc-800 px-3 py-2 rounded">
              {setupData?.secret}
            </code>
            <Button
              variant="ghost"
              size="sm"
              onClick={copySecret}
              className="text-zinc-400 hover:text-white"
            >
              <Copy className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Verification Code Input */}
        <div className="space-y-2">
          <Label className="text-zinc-300">Enter verification code</Label>
          <Input
            type="text"
            placeholder="000000"
            value={totpCode}
            onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            className="bg-zinc-800 border-zinc-700 text-white text-center text-2xl tracking-widest"
            maxLength={6}
          />
        </div>

        <div className="flex gap-3">
          <Button
            variant="ghost"
            className="text-zinc-400"
            onClick={() => {
              setStep('main');
              setSetupData(null);
              setTotpCode('');
            }}
          >
            Cancel
          </Button>
          <Button
            className="bg-emerald-600 hover:bg-emerald-700 flex-1"
            onClick={handleVerify2FA}
            disabled={loading || totpCode.length !== 6}
          >
            {loading ? 'Verifying...' : (
              <>
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Enable 2FA
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  const renderDisable = () => (
    <Card className="bg-zinc-800/50 border-zinc-700">
      <CardHeader>
        <CardTitle className="text-lg text-red-400 flex items-center gap-2">
          <ShieldOff className="w-5 h-5" />
          Disable Two-Factor Authentication
        </CardTitle>
        <CardDescription className="text-zinc-400">
          Enter your current 2FA code to disable
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
          <p className="text-sm text-red-400">
            Warning: Disabling 2FA will make your account less secure. 
            Anyone with your password will be able to access your account.
          </p>
        </div>

        <div className="space-y-2">
          <Label className="text-zinc-300">Current 2FA Code</Label>
          <Input
            type="text"
            placeholder="000000"
            value={totpCode}
            onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            className="bg-zinc-800 border-zinc-700 text-white text-center text-2xl tracking-widest"
            maxLength={6}
          />
        </div>

        <div className="flex gap-3">
          <Button
            variant="ghost"
            className="text-zinc-400"
            onClick={() => {
              setStep('main');
              setTotpCode('');
            }}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            className="bg-red-600 hover:bg-red-700"
            onClick={handleDisable2FA}
            disabled={loading || totpCode.length !== 6}
          >
            {loading ? 'Disabling...' : 'Disable 2FA'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-zinc-950 p-4 sm:p-6 lg:p-8">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/dashboard')}
            className="text-zinc-400 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'var(--font-heading)' }}>
            Settings
          </h1>
        </div>

        {/* Content */}
        {step === 'main' && renderMain()}
        {step === 'setup' && renderSetup()}
        {step === 'verify' && renderVerify()}
        {step === 'disable' && renderDisable()}
      </div>
    </div>
  );
}
